/**
 * Approximate text widths for the PDF base fonts.
 *
 * Courier is exactly 0.6 em per character, so mono wrapping is precise. The
 * proportional numbers are close enough for the one job they have — deciding
 * where to truncate a title — and are never used to position anything. If a
 * title is clipped a character early, nobody drowns.
 */

import type { FontFamily } from "./ops";

const NARROW: Record<string, number> = {
  " ": 278, "!": 278, '"': 355, "'": 191, "(": 333, ")": 333, "*": 389,
  ",": 278, "-": 333, ".": 278, "/": 278, ":": 278, ";": 278, "[": 278,
  "]": 278, "f": 278, "i": 222, "j": 222, "l": 222, "r": 333, "t": 278,
  "I": 278, "J": 500, "·": 333,
};

const WIDE: Record<string, number> = {
  m: 833, w: 722, M: 833, W: 944, "%": 889, "@": 1015,
};

function proportionalWidth(ch: string, bold: boolean): number {
  const w = NARROW[ch] ?? WIDE[ch] ?? (ch >= "A" && ch <= "Z" ? 700 : 556);
  return bold ? w * 1.06 : w;
}

/** Width of `text` in points. */
export function measure(
  text: string,
  family: FontFamily,
  size: number,
  weight: "normal" | "bold" = "normal",
  tracking = 0,
): number {
  if (family === "mono") return text.length * size * 0.6 + text.length * tracking;
  let units = 0;
  for (const ch of text) units += proportionalWidth(ch, weight === "bold");
  return (units / 1000) * size + text.length * tracking;
}

/** Trim to fit, with an ellipsis if anything was lost. */
export function truncate(
  text: string,
  maxWidth: number,
  family: FontFamily,
  size: number,
  weight: "normal" | "bold" = "normal",
  tracking = 0,
): string {
  if (measure(text, family, size, weight, tracking) <= maxWidth) return text;
  let out = text;
  while (out.length > 1 && measure(out + "…", family, size, weight, tracking) > maxWidth) {
    out = out.slice(0, -1);
  }
  return out.trimEnd() + "…";
}

/**
 * Hard-wrap a monospaced string. The Labs payload has no spaces to break on, so
 * this breaks on the character grid — which is what you want anyway, because a
 * command printed in even columns is one you can read back into a keyboard.
 */
export function wrapMono(text: string, maxWidth: number, size: number): string[] {
  const perLine = Math.max(1, Math.floor(maxWidth / (size * 0.6)));
  const lines: string[] = [];
  for (let i = 0; i < text.length; i += perLine) lines.push(text.slice(i, i + perLine));
  return lines;
}
